// @greenlight-spec: 57-screenshot-walkthroughs.md, 28-it-dashboard-ui.md
import { z } from 'zod';

/**
 * The shape of `public/walkthroughs/<id>/walkthrough.json`. Only the shape is
 * enforced: the text fields are prose for a person and for the agent that
 * re-verifies the walkthrough, and `pnpm walkthroughs:lint` carries the
 * content checks (images exist, alt text, size, no email addresses, age).
 */
export const walkthroughStepSchema = z.object({
  id: z.string().min(1),
  /** May contain `{placeholder}` tokens the rendering page fills in. */
  instruction: z.string().min(1),
  /** File name inside the walkthrough folder, `NN-<slug>.png`. */
  image: z.string().min(1).optional(),
  alt: z.string().min(1).optional(),
});

export const walkthroughSchema = z.object({
  id: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'kebab-case id'),
  title: z.string().min(1),
  /** The surface the person acts on, as the vendor names it. */
  vendor: z.string().min(1),
  /**
   * `external`: steps on a surface Greenlight does not control, rendered by
   * the dashboard and the docs. `greenlight`: a picture of Greenlight itself
   * (the dashboard, the installer GUI) for the docs.
   */
  surface: z.enum(['external', 'greenlight']).default('external'),
  audience: z.enum(['it', 'builder']),
  /** Absent for a desktop app, the installer GUI, a CLI, or a Greenlight capture. */
  startUrl: z.string().url().optional(),
  /**
   * Greenlight captures only: where the picture comes from, so it can be taken
   * again. `mock` is the local ui-mock (scenario, route); `install` is a
   * Shift-owned Greenlight install (named, with the route); `installer` is the
   * installer GUI.
   */
  source: z
    .object({
      kind: z.enum(['mock', 'install', 'installer']),
      scenario: z.string().min(1).optional(),
      install: z.string().min(1).optional(),
      route: z.string().min(1).optional(),
    })
    .optional(),
  prerequisites: z.array(z.string().min(1)).default([]),
  steps: z.array(walkthroughStepSchema).min(1),
  /** For the re-verifying agent: what to look for, what needs a human. */
  notes: z.string().default(''),
  verified: z.object({
    on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
    notes: z.string().default(''),
  }),
});

export type WalkthroughStep = z.infer<typeof walkthroughStepSchema>;
export type Walkthrough = z.infer<typeof walkthroughSchema>;
export type WalkthroughInput = z.input<typeof walkthroughSchema>;
